/* IntegrationsRenderer.js */

"use strict";
const _ = require("lodash");
const Steve = require("@gluenetworks/steve_core").getInstance();
const uiCore = require("@gluenetworks/steve_ui_core");
const InputTextControl = uiCore.Control.Textfield.InputText;
const CheckboxControl = uiCore.Control.Boolean.InputCheckbox;
const TextArea = uiCore.Control.Textfield.Textarea;
const SingleComboBox = uiCore.Control.OptionSelection.Single.ComboBox;
const ScheduleComponent = require("@gluenetworks/schedule_component");
const ButtonControl = uiCore.Control.Action.Button;

class IntegrationsRenderer {
    constructor(model, uiUtils) {
        this._model = model;
        this._uiUtils = uiUtils;
        this._rootElem = document.createElement("integrations-gl");

        this._ciscoEnableCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Enable Cisco API",
            title: "Allow Gluware to retrieve updates from the Cisco API",
        });

        this._ciscoConnectionComboBox = new SingleComboBox({
            darkTheme: true,
            disabled: this._ciscoEnableCheckbox.value || true,
        });

        this._testCiscoConnectivity = new ButtonControl({
            label: "Test Cisco Support API Connectivity",
            title: "Make sure Gluware can connect to the Cisco API Console",
            darkTheme: true,
            disabled: this._ciscoEnableCheckbox.value || true,
        });

        this._testCiscoConnectivity.renderer
            .getRootElement()
            .classList.add("test-cisco-connectivity");

        this._addNewConnectionButton = new ButtonControl({
            label: "Add Connection",
            darkTheme: true,
            class: "add-connection-button",
        });

        this._ciscoNoConnectionsEl = this._getNoConnectionElement("Cisco");

        this._scheduleEnabledCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Enable Schedule",
            title: "Set up a schedule for retrieving updates",
            disabled: this._ciscoEnableCheckbox.value || true,
        });

        this._scheduleComponent = new ScheduleComponent(undefined, {
            allowOne: false,
            allowRepeat: true,
            pollOnly: false,
            availOnly: true,
            disabled:
                (this._ciscoEnableCheckbox.value && this._scheduleEnabledCheckbox.value) || true,
            noStartingEnding: true,
        });
        this._scheduleComponent.theme = "dark";
        this._scheduleComponent.setScheduleProp(
            "time",
            new Date(new Date().setHours(23, 59, 59, 999))
        );

        this._nistEnableCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Enable NVD API",
            title: "Allow Gluware to retrieve updates from NIST NVD API",
        });

        this._nistCredentialsCmbBox = new SingleComboBox({
            darkTheme: true,
            allowClear: true,
            disabled: this._nistEnableCheckbox.value || true,
        });

        this._testNISTConnectivity = new ButtonControl({
            label: "Test NVD API Connectivity",
            title: "Make sure Gluware can connect to the NIST NVD API",
            darkTheme: true,
            disabled: this._ciscoEnableCheckbox.value || true,
        });

        /* Stackstorm */

        this._ssEnableCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Enable StackStorm",
            title: "Allow Gluware to retrieve updates from the StackStorm API",
        });

        this._ssCredentialsCmbBox = new SingleComboBox({
            darkTheme: true,
        });

        this._ssUrlInput = new InputTextControl({
            darkTheme: true,
            disabled: this._ssEnableCheckbox.value || true,
        });
        // this._clientSecretInput.setAttribute("type", "password");

        this._ssUseSSL = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Use SSL",
            title: "Use SSL for Stackstorm",
        });

        this._ssSkipServerCheck = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Skip server identity check",
            title: "Skip server identity check",
        });

        this._ssCACert = new TextArea({
            darkTheme: true,
        });

        // Git Support
        this._gsEnableLocalGitCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Enable Local Repository",
            title: "Enable a local Repository",
        });
        this._gsEnableRemoteGitCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Enable Remote Repository",
            title: "Enable a remote Repository",
        });

        this._gRCCmbBox = new SingleComboBox({
            darkTheme: true,
        });

        this._testRemoteGitConnectivity = new ButtonControl({
            label: "Test Remote VCS Connectivity",
            title: "Make sure Gluware can connect to the Remote Repository",
            darkTheme: true,
            disabled: true,
        });

        this._initiateVersionControl = new ButtonControl({
            label: "Sync Data",
            title: "Sync data with the local repository",
            darkTheme: true,
            class: "initialize-version-control-btn",
        });

        this._gsEnableScheduleCheckbox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Enable Schedule",
            title: "Enable a schedule",
        });
        this._gRScheduleComponent = new ScheduleComponent(undefined, {
            allowOne: false,
            allowRepeat: true,
            pollOnly: false,
            availOnly: true,
            disabled: false,
            noStartingEnding: true,
        });
        this._defaultSchedule = ScheduleComponent.defaultValue({
            allowOne: false,
            allowRepeat: true,
            pollOnly: false,
            availOnly: true,
        });
        this._gRScheduleComponent.theme = "dark";
        this._gRScheduleComponent.setScheduleProp(
            "time",
            new Date(new Date().setHours(23, 59, 59, 999))
        );

        this._versionControlNoConnectionsEl = this._getNoConnectionElement("Version Control");

        this._testSSConnectivity = new ButtonControl({
            label: "Test StackStorm API Connectivity",
            title: "Make sure Gluware can connect to the StackStorm API",
            darkTheme: true,
            disabled: this._ssEnableCheckbox.value || true,
        });

        this._testSSConnectivity.renderer.getRootElement().classList.add("test-ss-connectivity");

        this._refreshSSPacks = new ButtonControl({
            label: "Reload StackStorm Packs",
            title: "Update available Network RPA catalogs according to the installed packs",
            darkTheme: true,
            disabled: this._ssEnableCheckbox.value || true,
        });

        this._refreshSSPacks.renderer.getRootElement().classList.add("refresh-ss-packs");

        /* ------- */

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            title: "Cancel",
            darkTheme: true,
            disabled: this._ciscoEnableCheckbox.value || true,
        });

        this._confirmButton = new ButtonControl({
            label: "Save",
            title: "Save",
            darkTheme: true,
            disabled: this._ciscoEnableCheckbox.value || true,
            active: true,
        });

        this._controlButtonsContainer = document.createElement("div");
        this._controlButtonsContainer.classList.add("control-buttons");
        this._controlButtonsContainer.appendChild(this._cancelButton.renderer.getRootElement());
        this._controlButtonsContainer.appendChild(this._confirmButton.renderer.getRootElement());
        this._handleConnectionsLinkClick = this._handleConnectionsLinkClick.bind(this);
        this._statusChanged = this._statusChanged.bind(this);

        //this._rootElem.appendChild(this._createGluSection());
        const heading = document.createElement("h1");
        heading.innerText = "Integrations";
        this._rootElem.appendChild(heading);
        const containerEl = document.createElement("div");
        containerEl.classList.add("container");
        containerEl.appendChild(this._createSupportSection());
        containerEl.appendChild(this._createGitSupportSection());
        // containerEl.appendChild(this._createNistSection());
        containerEl.appendChild(this._createStackStormSection());
        // containerEl.appendChild(this._createScheduleSection());
        this._rootElem.appendChild(containerEl);
        this._rootElem.appendChild(this._controlButtonsContainer);
        // this._isDefaultSchedule = true;
        this._resetScheduleToDefault = this._resetScheduleToDefault.bind(this);
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _shutdown() {
        this._unregisterListeners();
        this._uiUtils.clearMessage();

        let promArr = [
            this._scheduleComponent.shutdown(),
            this._scheduleEnabledCheckbox.shutdown(),
            this._ciscoEnableCheckbox.shutdown(),
            this._ciscoConnectionComboBox.shutdown(),
            this._testCiscoConnectivity.shutdown(),
            this._addNewConnectionButton.shutdown(),
            this._ssEnableCheckbox.shutdown(),
            this._ssUseSSL.shutdown(),
            this._ssSkipServerCheck.shutdown(),
            this._ssCredentialsCmbBox.shutdown(),
            this._ssUrlInput.shutdown(),
            this._ssCACert.shutdown(),
            this._testSSConnectivity.shutdown(),
            this._refreshSSPacks.shutdown(),
            this._nistEnableCheckbox.shutdown(),
            this._nistCredentialsCmbBox.shutdown(),
            this._testNISTConnectivity.shutdown(),
            this._cancelButton.shutdown(),
            this._confirmButton.shutdown(),
        ];

        return Promise.all(promArr)
            .then(() => {
                this._model = undefined;
                this._ciscoEnableCheckbox = undefined;
                this._ciscoConnectionComboBox = undefined;
                this._testCiscoConnectivity = undefined;
                this._addNewConnectionButton = undefined;
                this._scheduleEnabledCheckbox = undefined;
                this._ciscoNoConnectionsEl = undefined;
                this._ciscoConnectionContainer = undefined;
                this._scheduleComponent = undefined;
                this._cancelButton = undefined;
                this._confirmButton = undefined;
                this._ssEnableCheckbox = undefined;
                this._ssSkipServerCheck = undefined;
                this._ssUseSSL = undefined;
                this._ssCredentialsCmbBox = undefined;
                this._ssUrlInput = undefined;
                this._ssCACert = undefined;
                this._testSSConnectivity = undefined;
                this._refreshSSPacks = undefined;
                this._controlButtonsContainer.remove();
                this._controlButtonsContainer = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            })
            .catch((err) => {
                console.log(err);
            });
    }

    _registerListeners() {
        this._ciscoEnableCheckbox.on(
            "UserValue",
            (this._ciscoEnableChangeHandler = this._ciscoEnableChangeHandler.bind(this))
        );
        this._nistEnableCheckbox.on(
            "UserValue",
            (this._nistEnableChangeHandler = this._nistEnableChangeHandler.bind(this))
        );
        this._ssEnableCheckbox.on(
            "UserValue",
            (this._ssEnableChangeHandler = this._ssEnableChangeHandler.bind(this))
        );
        this._nistCredentialsCmbBox.on(
            "UserValue",
            (this._nistCredentialsChanged = this._nistCredentialsChanged.bind(this))
        );
        this._confirmButton.on("UserClick", (this._confirmAction = this._confirmAction.bind(this)));
        this._cancelButton.on("UserClick", (this._cancelAction = this._cancelAction.bind(this)));
        this._scheduleEnabledCheckbox.on(
            "UserValue",
            (this._scheduleEnabledHandler = this._scheduleEnabledHandler.bind(this))
        );
        this._scheduleComponent.on(
            "UserValue",
            (this._scheduleChanged = this._scheduleChanged.bind(this))
        );
        this._ciscoConnectionComboBox.on(
            "UserValue",
            (this._ciscoConnectionChanged = this._ciscoConnectionChanged.bind(this))
        );

        this._ssCredentialsCmbBox.on(
            "UserValue",
            (this._ssCredentialsChanged = this._ssCredentialsChanged.bind(this))
        );
        this._ssUrlInput.on("UserValue", (this._ssUrlChanged = this._ssUrlChanged.bind(this)));
        this._ssUseSSL.on(
            "UserValue",
            (this.__ssUseSSLChanged = this.__ssUseSSLChanged.bind(this))
        );
        this._ssSkipServerCheck.on(
            "UserValue",
            (this._ssSkipServerCheckChanged = this._ssSkipServerCheckChanged.bind(this))
        );

        this._ssCACert.on("UserValue", (this._ssCACertChanged = this._ssCACertChanged.bind(this)));

        this._testCiscoConnectivity.on(
            "UserClick",
            (this._testCiscoConnectivityAction = this._testCiscoConnectivityAction.bind(this))
        );

        this._addNewConnectionButton.on(
            "UserClick",
            (this._newConnectionAction = this._newConnectionAction.bind(this))
        );

        this._testNISTConnectivity.on(
            "UserClick",
            (this._testNistConnectivityAction = this._testNistConnectivityAction.bind(this))
        );
        this._testSSConnectivity.on(
            "UserClick",
            (this._testSSConnectivityAction = this._testSSConnectivityAction.bind(this))
        );
        this._refreshSSPacks.on(
            "UserClick",
            (this._refreshSSPacksAction = this._refreshSSPacksAction.bind(this))
        );
        this.model.on(
            "IntegrationsOrganizationChanged",
            (this._onIntegrationsOrganizationChanged =
                this._onIntegrationsOrganizationChanged.bind(this))
        );
        this._gsEnableLocalGitCheckbox.on(
            "UserValue",
            (this._gsEnableLocalChangeHandler = this._gsEnableLocalChangeHandler.bind(this))
        );
        this._gsEnableRemoteGitCheckbox.on(
            "UserValue",
            (this._gsEnableRemoteChangeHandler = this._gsEnableRemoteChangeHandler.bind(this))
        );
        this._gsEnableScheduleCheckbox.on(
            "UserValue",
            (this._gsEnableScheduleChangeHandler = this._gsEnableScheduleChangeHandler.bind(this))
        );
        this._gRScheduleComponent.on(
            "UserValue",
            (this._gsScheduleChanged = this._gsScheduleChanged.bind(this))
        );
        this._gRCCmbBox.on(
            "UserValue",
            (this._gsCredentialsChanged = this._gsCredentialsChanged.bind(this))
        );
        this._initiateVersionControl.on(
            "UserClick",
            (this._initiateVersionControlAction = this._initiateVersionControlAction.bind(this))
        );
        this._testRemoteGitConnectivity.on(
            "UserClick",
            (this._testGSConnectivityAction = this._testGSConnectivityAction.bind(this))
        );

        this._connectionsLinkEl.addEventListener("click", this._handleConnectionsLinkClick);
        this.model.on("VersionControlConfigStatusPropertyUpdated", this._statusChanged);
    }

    _unregisterListeners() {
        this._ciscoEnableCheckbox.removeListener("UserValue", this._ciscoEnableChangeHandler);
        this._nistEnableCheckbox.removeListener("UserValue", this._nistEnableChangeHandler);
        this._nistCredentialsCmbBox.removeListener("UserValue", this._nistCredentialsChanged);
        this._confirmButton.removeListener("UserClick", this._confirmAction);
        this._cancelButton.removeListener("UserClick", this._cancelAction);
        this._scheduleEnabledCheckbox.removeListener("UserValue", this._scheduleEnabledHandler);
        this._scheduleComponent.removeListener("UserValue", this._scheduleChanged);
        this._ciscoConnectionComboBox.removeListener("UserValue", this._ciscoConnectionChanged);
        this._testCiscoConnectivity.removeListener("UserClick", this._testCiscoConnectivityAction);
        this._addNewConnectionButton.removeListener("UserClick", this._newConnectionAction);
        this._testNISTConnectivity.removeListener("UserClick", this._testNistConnectivityAction);
        this._ssEnableCheckbox.removeListener("UserValue", this._ssEnableChangeHandler);
        this._ssUseSSL.removeListener("UserValue", this.__ssUseSSLChanged);
        this._ssSkipServerCheck.removeListener("UserValue", this._ssSkipServerCheckChanged);
        this._ssCredentialsCmbBox.removeListener("UserValue", this._ssCredentialsChanged);
        this._ssUrlInput.removeListener("UserValue", this._ssUrlChanged);
        this._ssCACert.removeListener("UserValue", this._ssCACertChanged);
        this._testSSConnectivity.removeListener("UserClick", this._testSSConnectivityAction);
        this._refreshSSPacks.removeListener("UserClick", this._refreshSSPacksAction);
        this.model.removeListener(
            "IntegrationsOrganizationChanged",
            this._onIntegrationsOrganizationChanged
        );
        this._gsEnableLocalGitCheckbox.removeListener(
            "UserValue",
            this._gsEnableLocalChangeHandler
        );
        this._gsEnableRemoteGitCheckbox.removeListener(
            "UserValue",
            this._gsEnableRemoteChangeHandler
        );
        this._gsEnableScheduleCheckbox.removeListener(
            "UserValue",
            this._gsEnableScheduleChangeHandler
        );
        this._initiateVersionControl.removeListener(
            "UserClick",
            this._initiateVersionControlAction
        );
        this._gRScheduleComponent.removeListener("UserValue", this._gsScheduleChanged);
        this._gRCCmbBox.removeListener("UserValue", this._gsCredentialsChanged);
        this._testRemoteGitConnectivity.removeListener("UserClick", this._testGSConnectivityAction);
        this._connectionsLinkEl.removeEventListener("click", this._handleConnectionsLinkClick);
        this.model.removeListener("VersionControlConfigStatusPropertyUpdated", this._statusChanged);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (this._model) {
            this.model.modalUserFeedback.displayLoading();
            return this._model.whenModelIsReady().then(() => {
                if (this._rootElem) {
                    this._registerListeners();
                    this._ciscoEnableCheckbox.setDisabled(
                        this._model._ciscoServiceConnectionEntities.length === 0
                    );
                    this._formAvailabilityToggle(!this._ciscoEnableCheckbox.value);
                }
                this._setFormValues();
                this._scheduleComponent.renderer.render();
                this._gRScheduleComponent.renderer.render();
                const isInitVersionControlDisabled = this._isInitVersionControlDisabled();
                this._initiateVersionControl.setDisabled(isInitVersionControlDisabled);
                this.model.modalUserFeedback.hideDisplayed();
            });
        }
    }

    _isInitVersionControlDisabled() {
        return (
            !this._model._versionControlConfigEntity.pristineProperties.localEnabled ||
            (!this._model._versionControlConfigEntity.localEnabled &&
                this._model._versionControlConfigEntity.pristineProperties.localEnabled) ||
            !this._model._versionControlConfigEntity.isSynchronized() ||
            this._model._versionControlConfigEntity.status === "INITIALIZING"
        );
    }

    _setFormValues() {
        //this._gluAPIEnabledCheckbox.value = this.model.getGluAPIEnabled();

        this._ciscoConnectionComboBox.setOptions(this._model._ciscoConnectionOptions);
        this._nistCredentialsCmbBox.setOptions(this._model._credentialsList);
        this._ssCredentialsCmbBox.setOptions(this._model._credentialsList);
        const vendorApiConfig = this.model.vendorApiConfig;

        if (typeof vendorApiConfig.scheduleEnabled !== "undefined") {
            this._scheduleEnabledCheckbox.setValue(vendorApiConfig.scheduleEnabled);
            if (vendorApiConfig.scheduleEnabled === false) {
                this._scheduleComponent.setDisabledSchedule(true);
            } else {
                if (vendorApiConfig.schedule) {
                    this._scheduleComponent.setSchedule(vendorApiConfig.schedule);
                } else {
                    this._resetScheduleToDefault();
                }
            }
        } else {
            // set defaults values for schedule
            this._scheduleEnabledCheckbox.setValue(false);
            this._resetScheduleToDefault();
        }
        this._scheduleComponent.setDisabledSchedule(!this._scheduleEnabledCheckbox.value);

        const cisco = _.get(vendorApiConfig, "properties.vendors.cisco");
        if (cisco) {
            const ciscoConnectionId = _.get(cisco, "serviceConnectionId", "");
            if (ciscoConnectionId !== this._ciscoConnectionComboBox.value) {
                this._ciscoConnectionComboBox.setValue(ciscoConnectionId);
            }
            const ciscoEnabled = _.get(cisco, "enabled");
            if (
                typeof ciscoEnabled !== "undefined" &&
                ciscoEnabled !== this._ciscoEnableCheckbox.value
            ) {
                this._ciscoEnableCheckbox.setValue(ciscoEnabled);
                this._formAvailabilityToggle(!ciscoEnabled);
            }
        } else {
            // Set default values to the form
            this._ciscoEnableCheckbox.setValue(false);
            this._ciscoConnectionComboBox.setValue(
                _.get(this._model._ciscoServiceConnectionEntities[0], "id", undefined)
            );
            this._formAvailabilityToggle(true);
        }

        const nist = _.get(vendorApiConfig, "properties.vendors.nist");
        if (nist) {
            const nistApiKey = _.get(nist, "apiKey") || undefined;
            if (nistApiKey !== this._nistCredentialsCmbBox.value) {
                this._nistCredentialsCmbBox.setValue(nistApiKey);
            }
            const nistEnabled = _.get(nist, "enabled");
            if (
                typeof nistEnabled !== "undefined" &&
                nistEnabled !== this._nistEnableCheckbox.value
            ) {
                this._nistEnableCheckbox.setValue(nistEnabled);
                this._formAvailabilityToggle(!nistEnabled, "nist");
            }
        } else {
            // Set default values to the form
            this._nistCredentialsCmbBox.setValue(undefined);
            this._nistEnableCheckbox.setValue(false);
            this._formAvailabilityToggle(true, "nist");
        }

        const stackstorm = _.get(vendorApiConfig, "properties.vendors.stackstorm", undefined);
        if (stackstorm) {
            const stackstormApiKey = _.get(stackstorm, "apiKey") || undefined;
            if (stackstormApiKey !== this._ssCredentialsCmbBox.value) {
                this._ssCredentialsCmbBox.setValue(stackstormApiKey);
            }
            if (stackstorm.apiUrl && stackstorm.apiUrl !== this._ssUrlInput.value) {
                this._ssUrlInput.setValue(stackstorm.apiUrl);
            }
            if (stackstorm.ssl && stackstorm.ssl !== this._ssUseSSL.value) {
                this._ssUseSSL.setValue(stackstorm.ssl);
            }
            if (
                stackstorm.skipIdentityCheck &&
                stackstorm.skipIdentityCheck !== this._ssSkipServerCheck.value
            ) {
                this._ssSkipServerCheck.setValue(stackstorm.skipIdentityCheck);
            }
            if (!stackstorm.ssl) {
                this._ssUseSSL.setValue(false);
            }
            if (!stackstorm.skipIdentityCheck) {
                this._ssSkipServerCheck.setValue(false);
            }
            if (
                stackstorm.customCACertificate &&
                stackstorm.customCACertificate !== this._ssCACert.value
            ) {
                this._ssCACert.setValue(stackstorm.customCACertificate);
            }
            if (!stackstorm.customCACertificate) {
                this._ssCACert.setValue("");
            }
            if (
                typeof stackstorm.enabled !== "undefined" &&
                stackstorm.enabled !== this._ssEnableCheckbox.value
            ) {
                this._ssEnableCheckbox.setValue(stackstorm.enabled);
                this._formAvailabilityToggle(!stackstorm.enabled, "ss");
            }
        } else {
            // Set default values to the form
            this._ssCredentialsCmbBox.setValue(undefined);
            this._ssUrlInput.setValue("");
            this._ssCACert.setValue("");
            this._ssUseSSL.setValue(false);
            this._ssSkipServerCheck.setValue(false);
            this._ssEnableCheckbox.setValue(false);
            this._formAvailabilityToggle(true, "ss");
        }

        // Git Values
        const gitLocalEnabled = this._model._versionControlConfigEntity.getLocalEnabled();
        const gitRemoteEnabled = this._model._versionControlConfigEntity.getRemoteEnabled();
        this._gsEnableLocalGitCheckbox.setValue(gitLocalEnabled);
        if (!gitLocalEnabled) {
            this._formAvailabilityToggle(true, "gs-l");
        } else {
            this._formAvailabilityToggle(false, "gs-l");
        }

        this._gsEnableRemoteGitCheckbox.setValue(gitRemoteEnabled);
        if (!gitRemoteEnabled) {
            this._formAvailabilityToggle(true, "gs-r");
        } else {
            this._formAvailabilityToggle(false, "gs-r");
        }
        this._gRCCmbBox.setOptions(this._model._connectionDropdownEntries || []);
        if (_.isEmpty(this._model._connectionDropdownEntries)) {
            this._gsRemoteContentEl.classList.add("hidden");
            this._versionControlNoConnectionsEl.classList.remove("hidden");
        } else {
            this._versionControlNoConnectionsEl.classList.add("hidden");
            this._gsRemoteContentEl.classList.remove("hidden");
        }
        this._gRCCmbBox.setValue(
            this._model._versionControlConfigEntity.getServiceConnectionId() ||
                (this._model._connectionDropdownEntries &&
                    this._model._connectionDropdownEntries[0]) ||
                []
        );

        if (this._model._versionControlConfigEntity.getSchedule()) {
            this._gsEnableScheduleCheckbox.setValue(true);
            this._gRScheduleComponent.setSchedule(
                this._model._versionControlConfigEntity.getSchedule()
            );
            this._formAvailabilityToggle(false, "gs-s");
        } else {
            this._gsEnableScheduleCheckbox.setValue(false);
            this._gRScheduleComponent.setSchedule(this._defaultSchedule);
            this._formAvailabilityToggle(true, "gs-s");
        }

        this._updateControlButtonsAvailability();

        return Promise.resolve();
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _updateControlButtonsAvailability() {
        const isVendorApiConfigDirty =
            this.model.vendorApiConfig.isDirty() && this._validatePendingPatches();
        const isVersionControlConfigDirty =
            this._model._versionControlConfigEntity.isDirty() &&
            this._validatePendingPatches(this._model._versionControlConfigEntity);

        let isFormValid = true;
        if (isVendorApiConfigDirty) {
            isFormValid = this._validateVendorApiConfigForm();
        }
        if (isVersionControlConfigDirty && isFormValid) {
            isFormValid = this._validateVersionControlConfigForm();
        }

        this._toggleControlButtonsBasedOnFormValidity(
            isFormValid,
            isVendorApiConfigDirty,
            isVersionControlConfigDirty
        );
    }

    _validateVendorApiConfigForm() {
        let isFormValid = true;
        if (this._ciscoEnableCheckbox.value) {
            isFormValid = this._validateCiscoForm();
        }
        if (this._ssEnableCheckbox.value && isFormValid) {
            isFormValid = this._validateSSForm();
        }
        return isFormValid;
    }

    _validateCiscoForm() {
        return !!this._ciscoConnectionComboBox.value;
    }

    _validateSSForm() {
        return (
            this._ssCredentialsCmbBox.value &&
            this._ssCredentialsCmbBox.value.trim() &&
            this._ssUrlInput.value &&
            this._ssUrlInput.value.trim()
        );
    }

    _validatePendingPatches(entity) {
        if (!entity) {
            entity = this.model.vendorApiConfig;
        }
        const pendingPatches = entity.pendingPatches;
        return !(
            pendingPatches &&
            pendingPatches.length === 1 &&
            pendingPatches[0].path === "/schedule" &&
            pendingPatches[0].op === "add"
        );
    }

    _validateVersionControlConfigForm() {
        return (
            !this._gsEnableLocalGitCheckbox.getValue() ||
            !this._gsEnableRemoteGitCheckbox.getValue() ||
            this._gRCCmbBox.getOptions().length > 0
        );
    }

    _toggleControlButtonsBasedOnFormValidity(
        isFormValid,
        isVendorApiConfigDirty,
        isVersionControlConfigDirty
    ) {
        const gitLocalEnabled = this._gsEnableLocalGitCheckbox.getValue();
        const gitRemoteEnabled = this._gsEnableRemoteGitCheckbox.getValue();
        const gitEnabled = gitLocalEnabled && gitRemoteEnabled;

        this._toggleControlButtons(
            isVendorApiConfigDirty || isVersionControlConfigDirty ? !isFormValid : isFormValid
        );

        if (gitEnabled) {
            this._testRemoteGitConnectivity.setDisabled(
                isVersionControlConfigDirty ? true : !isFormValid
            );
        } else {
            this._testRemoteGitConnectivity.setDisabled(true);
        }
    }

    _formAvailabilityToggle(isDisabled, type = "cisco") {
        if (type === "nist") {
            this._nistCredentialsCmbBox.setDisabled(isDisabled);
            this._testNISTConnectivity.disabled = isDisabled;
        } else if (type === "ss") {
            this._ssCredentialsCmbBox.setDisabled(isDisabled);
            this._ssUrlInput.disabled = isDisabled;
            this._ssCACert.disabled = isDisabled;
            this._testSSConnectivity.disabled = isDisabled;
            this._refreshSSPacks.disabled = isDisabled;
        } else if (type === "gs-l") {
            this._gsEnableRemoteGitCheckbox.setDisabled(isDisabled);

            this._gRCCmbBox.setDisabled(!this._gsEnableRemoteGitCheckbox.getValue());
            if (
                this._gRCCmbBox.getOptions().length > 0 &&
                !this._gsEnableRemoteGitCheckbox.isDisabled()
            ) {
                this._testRemoteGitConnectivity.setDisabled(
                    !this._gsEnableRemoteGitCheckbox.getValue()
                );
            }
            this._gsEnableScheduleCheckbox.setDisabled(isDisabled);
            this._gRScheduleComponent.setDisabledSchedule(
                !this._gsEnableScheduleCheckbox.getValue()
            );
        } else if (type === "gs-r") {
            this._gRCCmbBox.setDisabled(isDisabled);
            if (this._gRCCmbBox.getOptions().length > 0) {
                this._testRemoteGitConnectivity.setDisabled(
                    !this._gsEnableRemoteGitCheckbox.getValue()
                );
            }
        } else if (type === "gs-s") {
            this._gRScheduleComponent.setDisabledSchedule(isDisabled);
        } else {
            if (this._model._ciscoServiceConnectionEntities.length > 0) {
                this._ciscoConnectionComboBox.setDisabled(isDisabled);
                this._testCiscoConnectivity.disabled = isDisabled;
                this._ciscoNoConnectionsEl.classList.add("hidden");
                this._ciscoConnectionContainer.classList.remove("hidden");
                this._addNewConnectionButton.renderer.getRootElement().classList.remove("hidden");
                this._testCiscoConnectivity.renderer.getRootElement().classList.remove("hidden");
            } else {
                this._ciscoConnectionComboBox.setDisabled(true);
                this._testCiscoConnectivity.disabled = true;
                this._ciscoNoConnectionsEl.classList.remove("hidden");
                this._ciscoConnectionContainer.classList.add("hidden");
                this._addNewConnectionButton.renderer.getRootElement().classList.add("hidden");
                this._testCiscoConnectivity.renderer.getRootElement().classList.add("hidden");
            }
        }
        this._toggleSchedule();
    }

    _toggleSchedule() {
        const toDisable = !(this._ciscoEnableCheckbox.value || this._nistEnableCheckbox.value);
        this._scheduleEnabledCheckbox.disabled = toDisable;
        if (this._scheduleEnabledCheckbox.value === true) {
            this._scheduleComponent.setDisabledSchedule(toDisable);
        }
    }

    _toggleControlButtons(isDisabled) {
        this._cancelButton.disabled = isDisabled;
        this._confirmButton.disabled = isDisabled;
    }

    _createGluSection() {
        const gluSection = document.createDocumentFragment();
        gluSection.appendChild(this._gluAPIEnabledCheckbox.renderer.getRootElement());

        return this._createSection(gluSection, {title: "GluAPI"});
    }

    _createScheduleSection() {
        const schSection = document.createDocumentFragment();
        const scheduleContainer = document.createElement("div");
        scheduleContainer.classList.add("eox-and-smartnet-schedule-container");
        scheduleContainer.appendChild(this._scheduleEnabledCheckbox.renderer.getRootElement());
        scheduleContainer.appendChild(this._scheduleComponent.renderer.getRootElement());
        schSection.appendChild(scheduleContainer);
        return this._createSection(schSection, {title: "", bordered: false});
    }

    _createSupportSection() {
        const supportSection = document.createDocumentFragment();
        supportSection.appendChild(this._createCiscoSection());
        supportSection.appendChild(this._createNistSection());
        supportSection.appendChild(this._createScheduleSection());
        return this._createSection(supportSection, {title: "Support Data", bordered: true});
    }

    _createCiscoSection() {
        const ciscoSection = document.createDocumentFragment();
        this._ciscoConnectionContainer = this._uiUtils.wrapElement(
            this._ciscoConnectionComboBox.renderer.getRootElement(),
            "Cisco Connection",
            "input-container"
        );
        this._ciscoConnectionContainer.title = "Cisco Connection for the API Console";
        this._ciscoConnectionContainer.classList.add("hidden");

        ciscoSection.appendChild(this._ciscoEnableCheckbox.renderer.getRootElement());
        ciscoSection.appendChild(this._ciscoConnectionContainer);
        ciscoSection.appendChild(this._ciscoNoConnectionsEl);
        ciscoSection.appendChild(this._addNewConnectionButton.renderer.getRootElement());
        ciscoSection.appendChild(this._testCiscoConnectivity.renderer.getRootElement());
        // ciscoSection.appendChild(scheduleContainer);

        return this._createSection(ciscoSection, {title: "Cisco API", bordered: true});
    }

    _createNistSection() {
        const nistSection = document.createDocumentFragment();
        const clientIDContainer = this._uiUtils.wrapElement(
            this._nistCredentialsCmbBox.renderer.getRootElement(),
            "NVD API Key (optional)",
            "input-container"
        );
        clientIDContainer.title = "NIST NVD API Key";

        // const scheduleContainer = document.createElement("div");
        // scheduleContainer.classList.add("eox-and-smartnet-schedule-container");
        // scheduleContainer.appendChild(this._scheduleEnabledCheckbox.renderer.getRootElement());
        // scheduleContainer.appendChild(this._scheduleComponent.renderer.getRootElement());

        nistSection.appendChild(this._nistEnableCheckbox.renderer.getRootElement());
        nistSection.appendChild(clientIDContainer);
        nistSection.appendChild(this._testNISTConnectivity.renderer.getRootElement());

        return this._createSection(nistSection, {title: "NIST NVD API", bordered: true});
    }

    _createStackStormSection() {
        const ssSection = document.createDocumentFragment();
        const clientIDContainer = this._uiUtils.wrapElement(
            this._ssCredentialsCmbBox.renderer.getRootElement(),
            "StackStorm API Key",
            "input-container"
        );
        clientIDContainer.title = "StackStorm API Key";
        const clientURLContainer = this._uiUtils.wrapElement(
            this._ssUrlInput.renderer.getRootElement(),
            "StackStorm URL",
            "input-container"
        );
        clientURLContainer.title = "StackStorm URL";
        const ssCACertContainer = this._uiUtils.wrapElement(
            this._ssCACert.renderer.getRootElement(),
            "Certificates (Required for SSL)",
            "input-container"
        );

        ssSection.appendChild(this._ssEnableCheckbox.renderer.getRootElement());
        ssSection.appendChild(clientURLContainer);
        ssSection.appendChild(clientIDContainer);
        ssSection.appendChild(this._ssUseSSL.renderer.getRootElement());
        ssSection.appendChild(this._ssSkipServerCheck.renderer.getRootElement());
        ssSection.appendChild(ssCACertContainer);
        ssSection.appendChild(this._testSSConnectivity.renderer.getRootElement());
        ssSection.appendChild(this._refreshSSPacks.renderer.getRootElement());
        // ssSection.appendChild(scheduleContainer);

        return this._createSection(ssSection, {title: "StackStorm", bordered: true});
    }

    _createGitSupportSection() {
        const gsSection = document.createDocumentFragment();

        gsSection.appendChild(this._gsEnableLocalGitCheckbox._renderer.getRootElement());
        gsSection.appendChild(this._createGitSupportRemoteSection());
        const gsScheduleSection = document.createElement("div");
        gsScheduleSection.classList.add("vc-schedule-container");
        gsScheduleSection.appendChild(this._gsEnableScheduleCheckbox._renderer.getRootElement());
        gsScheduleSection.appendChild(this._gRScheduleComponent._renderer.getRootElement());
        gsSection.appendChild(gsScheduleSection);
        gsSection.appendChild(this._initiateVersionControl._renderer.getRootElement());
        return this._createSection(gsSection, {
            title: "Version Control System Support",
            bordered: true,
        });
    }

    _createGitSupportRemoteSection() {
        const gsRemoteSection = document.createDocumentFragment();

        this._gsRemoteContentEl = document.createElement("div");
        this._gsRemoteContentEl.classList.add("gs-remote-content");
        const gitRemoteConnContainer = this._uiUtils.wrapElement(
            this._gRCCmbBox.renderer.getRootElement(),
            "Remote VCS Connection",
            "input-container"
        );

        this._gsRemoteContentEl.appendChild(
            this._gsEnableRemoteGitCheckbox._renderer.getRootElement()
        );
        this._gsRemoteContentEl.appendChild(gitRemoteConnContainer);
        this._gsRemoteContentEl.appendChild(
            this._testRemoteGitConnectivity._renderer.getRootElement()
        );

        gsRemoteSection.appendChild(this._gsRemoteContentEl);
        gsRemoteSection.appendChild(this._versionControlNoConnectionsEl);

        return this._createSection(gsRemoteSection, {
            title: "Remote VCS",
            bordered: true,
        });
    }

    _createSection(content, options = {}) {
        const section = document.createElement("section");
        section.className = options.className || "";
        section.classList.toggle("bordered", !!options.bordered);

        if (options.title) {
            const title = document.createElement("h2");
            title.innerText = options.title;
            section.appendChild(title);
        }
        section.appendChild(content);
        return section;
    }

    _getNoConnectionElement(type = "Cisco") {
        const noConnectionElement = document.createElement("div");
        noConnectionElement.classList.add("no-connection");
        this._connectionsLinkEl = document.createElement("a");
        this._connectionsLinkEl.classList.add("connections-link", "hyperlink");
        this._connectionsLinkEl.innerText = "Connections View";
        noConnectionElement.innerHTML = `No Connections available for ${type}.  Please create one in the&nbsp;`;
        noConnectionElement.appendChild(this._connectionsLinkEl);
        const periodEl = document.createElement("span");
        periodEl.innerText = ".";
        noConnectionElement.appendChild(periodEl);
        return noConnectionElement;
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _ciscoEnableChangeHandler(value) {
        if (typeof value === "boolean") {
            if (value === true) {
                this._uiUtils
                    .showConfirmDialog({
                        message:
                            "Enabling this feature constitutes acceptance and understanding that Gluware will collect data from a 3rd Party. Gluware, Inc. is not liable or responsible for the accuracy, timeliness or usefulness of the data collected from the 3rd Party and presented in the Gluware application.",
                        confirmButtonName: "Confirm",
                        cancelButtonName: "Cancel",
                    })
                    .then(
                        () => {
                            this._formAvailabilityToggle(!value);
                            this._setVendorConfig(true);
                            this._updateControlButtonsAvailability();
                        },
                        () => {
                            // Cancelled
                            if (!this._ciscoEnableCheckbox._value) {
                                this._setVendorConfig(false);
                                this._updateControlButtonsAvailability();
                            }
                            this._ciscoEnableCheckbox.setValue(false);
                        }
                    );
            } else {
                this._formAvailabilityToggle(!value);
                this._setVendorConfig(false);
                this._updateControlButtonsAvailability();
            }
        }
    }

    _ssEnableChangeHandler(value) {
        if (typeof value === "boolean") {
            if (value === true) {
                this._uiUtils
                    .showConfirmDialog({
                        message:
                            "Enabling StackStorm constitutes acceptance and understanding that Gluware will communicate and transfer information to an external StackStorm instance. Gluware, Inc. is not liable or responsible for the maintenance or setup of StackStorm.",
                        confirmButtonName: "Confirm",
                        cancelButtonName: "Cancel",
                    })
                    .then(
                        () => {
                            this._formAvailabilityToggle(!value, "ss");
                            this._setVendorConfig(true, "ss");
                            this._updateControlButtonsAvailability();
                        },
                        () => {
                            // Cancelled
                            if (!this._ssEnableCheckbox._value) {
                                this._setVendorConfig(false, "ss");
                                this._updateControlButtonsAvailability();
                            }
                            this._ssEnableCheckbox.setValue(false);
                        }
                    );
            } else {
                this._formAvailabilityToggle(!value, "ss");
                this._setVendorConfig(false, "ss");
                this._updateControlButtonsAvailability();
            }
        }
    }

    _nistEnableChangeHandler(value) {
        if (typeof value === "boolean") {
            if (value === true) {
                this._uiUtils
                    .showConfirmDialog({
                        message:
                            "Enabling this feature constitutes acceptance and understanding that Gluware will collect data from a 3rd Party. Gluware, Inc. is not liable or responsible for the accuracy, timeliness or usefulness of the data collected from the 3rd Party and presented in the Gluware application.",
                        confirmButtonName: "Confirm",
                        cancelButtonName: "Cancel",
                    })
                    .then(
                        () => {
                            this._formAvailabilityToggle(!value, "nist");
                            this._setVendorConfig(true, "nist");
                            this._updateControlButtonsAvailability();
                        },
                        () => {
                            if (!this._nistEnableCheckbox._value) {
                                this._setVendorConfig(false, "nist");
                                this._updateControlButtonsAvailability();
                            }
                            this._nistEnableCheckbox.setValue(false);
                        }
                    );
            } else {
                this._formAvailabilityToggle(!value, "nist");
                this._setVendorConfig(false, "nist");
                this._updateControlButtonsAvailability();
            }
        }
    }

    _nistCredentialsChanged() {
        this._setVendorConfig(true, "nist");
        this._updateControlButtonsAvailability();
        return true;
    }

    _scheduleEnabledHandler(enable) {
        this._scheduleComponent.setDisabledSchedule(!enable);
        this.model.vendorApiConfig.scheduleEnabled = enable;
        //set default schedule for the Entity
        this.model.vendorApiConfig.setSchedule(this._scheduleComponent.getSchedule());
        this._updateControlButtonsAvailability();
        return true;
    }

    _ciscoConnectionChanged() {
        this._setVendorConfig(true);
        this._updateControlButtonsAvailability();
        return true;
    }

    _ssCredentialsChanged() {
        this._setVendorConfig(true, "ss");
        this._updateControlButtonsAvailability();
        return true;
    }

    _ssUrlChanged() {
        this._setVendorConfig(true, "ss");
        this._updateControlButtonsAvailability();
        return true;
    }

    __ssUseSSLChanged() {
        this._setVendorConfig(true, "ss");
        this._updateControlButtonsAvailability();
        return true;
    }

    _ssSkipServerCheckChanged() {
        this._setVendorConfig(true, "ss");
        this._updateControlButtonsAvailability();
        return true;
    }

    _ssCACertChanged() {
        this._setVendorConfig(true, "ss");
        this._updateControlButtonsAvailability();
        return true;
    }

    _scheduleChanged() {
        this.model.vendorApiConfig.setSchedule(this._scheduleComponent.getSchedule());
        this._updateControlButtonsAvailability();
        return true;
    }

    _confirmAction() {
        let saveProm = [],
            isVendorApiUpdate = false;

        if (this._model.vendorApiConfig.isDirty()) {
            saveProm.push(this._model.vendorApiConfig.update());
            isVendorApiUpdate = true;
        }

        const versionControlConfigPendingPatches =
            this._model._versionControlConfigEntity.getPendingPatches();
        if (!_.isEmpty(versionControlConfigPendingPatches)) {
            if (this._model._versionControlConfigEntity.isSynchronized()) {
                saveProm.push(this._model._versionControlConfigEntity.update());
            } else {
                saveProm.push(this._model._versionControlConfigEntity.create());
            }
        }
        Promise.all(saveProm)
            .then(() => {
                if (
                    isVendorApiUpdate &&
                    this.model.vendorApiConfig.pendingRelationships &&
                    this.model.vendorApiConfig.pendingRelationships.length > 0
                ) {
                    return this.model.vendorApiConfig.updateRelationships();
                }
                return Promise.resolve();
            })
            .then(() => {
                if (isVendorApiUpdate) {
                    if (this._scheduleEnabledCheckbox.disabled) {
                        this.model._vendorApiConfigEntity.removeCustomFields(
                            this.model.activeOrgId
                        );
                    } else {
                        this.model._vendorApiConfigEntity.createCustomFields(
                            this.model.activeOrgId
                        );
                    }
                }
            })
            .then(() => {
                Steve.controlAppNav.updateHomebarVersionControl();
                const isInitVersionControlDisabled = this._isInitVersionControlDisabled();
                this._initiateVersionControl.setDisabled(isInitVersionControlDisabled);
                this._updateControlButtonsAvailability();
                return Promise.resolve();
            })
            .catch((err) => {
                if (_.get(err, "errorCode") !== "NoPendingUpdates") {
                    if (err.errorMsg) {
                        this._uiUtils.showMessageDialog({
                            message: "An error occurred: " + this._uiUtils.errorCodeMapping(err),
                            confirmButtonName: "OK",
                        });
                    } else {
                        this._uiUtils.writeErrorMessage("Validation " + err);
                    }
                }
            });
    }

    _cancelAction() {
        this.model
            .resetVendorApiConfigData()
            .then(() => {
                return this._model.resetVersionConfigData();
            })
            .then(() => {
                this._setFormValues();
            })
            .catch((err) => {
                this.model._logger.warn(err, "IntegrationsRender._cancelAction() error.");
            });
    }

    async _newConnectionAction() {
        const routeDetails = this._model._router.getRouteDetails();
        routeDetails.sr = "Organization/Connections";

        if (
            (this._validatePendingPatches() && this.model.vendorApiConfig.isDirty()) ||
            (this._validatePendingPatches(this._model._versionControlConfigEntity) &&
                this._model._versionControlConfigEntity.isSynchronized() &&
                this._model._versionControlConfigEntity.isDirty())
        ) {
            try {
                await this._model._modalUserFeedback.displayConfirmation(
                    "You have unsaved changes on this page. If you navigate away, your changes will be lost. Do you want to proceed?",
                    {
                        title: "Unsaved Changes",
                        confirmDetails: {
                            text: "OK",
                            type: "destructive",
                        },
                        rejectDetails: {
                            text: "Cancel",
                            type: "text",
                        },
                    }
                );
                this._model._router.goTo(routeDetails);
            } catch (err) {
                if (!err.userRejected) {
                    console.warn(err);
                }
            }
        } else {
            this._model._router.goTo(routeDetails);
        }
    }

    _testCiscoConnectivityAction() {
        const serviceConnectionId = this._ciscoConnectionComboBox.getValue();
        // Active org, not the connection's owning org: an inherited connection's tenant is an
        // ancestor the caller may not be authorized to launch work against.
        const workTarget = {
            category: "entity",
            entityType: "ServiceConnection",
            method: "single",
            id: serviceConnectionId,
        };
        this._model._serviceConnectionEntityDef
            .runTestConnectionWork(this._model.activeOrgId, workTarget)
            .then((response) => {
                this._uiUtils.showMessageDialog({
                    message:
                        response.summary.connected && response.summary.connected === true
                            ? "Successfully connected to Cisco Support API."
                            : "Failed to connect to Cisco Support API.",
                    confirmButtonName: "OK",
                });
            })
            .catch((err) => {
                this._uiUtils.showMessageDialog({
                    message: "Failed to connect to Cisco Support API.",
                    confirmButtonName: "OK",
                });
                this.model._logger.warn(
                    err,
                    "IntegrationsRender._testCiscoConnectivityAction() error."
                );
            });
    }

    _testNistConnectivityAction() {
        this.model.vendorApiConfigEntity
            .testNistConnectivity(
                this.model._userSession.activeOrganization.id,
                this._nistCredentialsCmbBox.value
            )
            .then((response) => {
                this._uiUtils.showMessageDialog({
                    message:
                        response.summary.connected && response.summary.connected === true
                            ? "Successfully connected to NIST NVD API."
                            : "Failed to connect to NIST NVD API.",
                    confirmButtonName: "OK",
                });
            })
            .catch((err) => {
                this._uiUtils.showMessageDialog({
                    message: "Failed to connect to NIST NVD API.",
                    confirmButtonName: "OK",
                });
                this.model._logger.warn(
                    err,
                    "IntegrationsRender._testNistConnectivityAction() error."
                );
            });
    }

    _testSSConnectivityAction() {
        this.model.vendorApiConfigEntity
            .testStackStormConnectivity(
                this.model._userSession.activeOrganization.id,
                this._ssUrlInput.value,
                this._ssCredentialsCmbBox.value,
                this._ssCACert.value,
                this._ssUseSSL.value,
                this._ssSkipServerCheck.value
            )
            .then((response) => {
                this._uiUtils.showMessageDialog({
                    message:
                        response.summary.connected && response.summary.connected === true
                            ? "Successfully connected to StackStorm."
                            : "Failed to connect to StackStorm.",
                    confirmButtonName: "OK",
                });
            })
            .catch((err) => {
                this._uiUtils.showMessageDialog({
                    message: "Failed to connect to StackStorm.",
                    confirmButtonName: "OK",
                });
                this.model._logger.warn(
                    err,
                    "IntegrationsRender._testSSConnectivityAction() error."
                );
            });
    }

    _testGSConnectivityAction() {
        this._model
            .testGitConnectivity(this._gRCCmbBox.getValue())
            .then(() => {
                this._uiUtils.showMessageDialog({
                    message: "Successfully connected to Remote Git.",
                    confirmButtonName: "OK",
                });
            })
            .catch((err) => {
                this._uiUtils.showMessageDialog({
                    message: "Failed to connect to Remote Git.",
                    confirmButtonName: "OK",
                });
                this.model._logger.warn(
                    err,
                    "IntegrationsRender._testSSConnectivityAction() error."
                );
            });
    }

    _handleConnectionsLinkClick(evt) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this._newConnectionAction();
    }

    _showProgressVeil() {
        return this.model.modalUserFeedback.displayLoading({
            message: "Reloading StackStorm Packs...",
        });
    }

    _progressCallBack({status, successCount, failureCount, targetCount}) {
        if (status.toLowerCase() !== "completed") {
            const msg = `Succeeded ${successCount}, failed ${failureCount} of ${targetCount} tasks`;
            return this.model.modalUserFeedback.showLoadingProgress(msg);
        }
    }

    _refreshSSPacksAction() {
        const workTarget = {
            category: "entity",
            entityType: "WorkflowTaskType",
            method: "single",
        };
        // TODO Reenable progressCb when work returns progress object
        // const progressCb = this._progressCallBack.bind(this);
        return this._showProgressVeil().then(() => {
            this.model._WorkflowTaskTypeEntity
                .runWorkflowTaskImportWork(
                    this.model._userSession.activeOrganization.id,
                    workTarget,
                    null,
                    null /*progressCb*/
                )
                .then((response) => {
                    this.model.modalUserFeedback.hideDisplayed();
                    this._uiUtils.showMessageDialog({
                        message: response.errorMsg
                            ? "Failed to refresh."
                            : "Successfully updated available Network RPA catalogs.",
                        confirmButtonName: "OK",
                    });
                })
                .catch((err) => {
                    this.model.modalUserFeedback.hideDisplayed();
                    this._uiUtils.showMessageDialog({
                        message: "Failed to refresh.",
                        confirmButtonName: "OK",
                    });
                    this.model._logger.warn(
                        err,
                        "IntegrationsRender._refreshSSPacksAction() error."
                    );
                });
        });
    }

    _gsEnableLocalChangeHandler(val) {
        if (typeof val === "boolean") {
            if (val === false) {
                this._uiUtils
                    .showDeleteConfirmDialog({
                        message:
                            "Disabling Version Control will remove your ability to commit saved configurations, view configuration history, or revert to previously committed versions. Are you sure you want to proceed?",
                        confirmButtonName: "Confirm",
                        cancelButtonName: "Cancel",
                    })
                    .then(
                        () => {
                            this._model._versionControlConfigEntity.setLocalEnabled(val);
                            this._formAvailabilityToggle(true, "gs-l");
                            this._setGitSupportData();
                        },
                        () => {
                            this._gsEnableLocalGitCheckbox.setValue(true);
                            this._model._versionControlConfigEntity.setLocalEnabled(true);
                            this._formAvailabilityToggle(false, "gs-l");
                            this._setGitSupportData();
                        }
                    );
            } else {
                this._model._versionControlConfigEntity.setLocalEnabled(val);
                this._formAvailabilityToggle(false, "gs-l");
                this._setGitSupportData();
            }
        }
        this._setGitSupportData();
    }

    _gsEnableRemoteChangeHandler(val) {
        if (!val) {
            this._formAvailabilityToggle(true, "gs-r");
        } else {
            this._formAvailabilityToggle(false, "gs-r");
        }
        this._setGitSupportData();
    }

    _gsEnableScheduleChangeHandler(val) {
        if (!val) {
            this._formAvailabilityToggle(true, "gs-s");
        } else {
            this._formAvailabilityToggle(false, "gs-s");
        }
        this._setGitSupportData();
    }

    _gsScheduleChanged() {
        this._setGitSupportData();
    }

    _gsCredentialsChanged() {
        this._setGitSupportData();
    }

    _initiateVersionControlAction() {
        this._statusChanged("INITIALIZING");
        this.model._versionControlConfigEntity
            .runInitializeWork()
            .then((response) => {
                if (response.errorMsg) {
                    throw new Error(response.errorMsg);
                }
                if (response.summary && response.summary.errorMsg) {
                    throw new Error(response.summary.errorMsg);
                }
                this._uiUtils.showMessageDialog({
                    message: "Successfully initialized version control.",
                    confirmButtonName: "OK",
                });
            })
            .catch((err) => {
                this._uiUtils.showMessageDialog({
                    message: `<p class="integ-vc-error-head">Failed to initialize version control</p>
                    <p class="integ-vc-error-message">${err.toString()}</p>`,
                    confirmButtonName: "OK",
                });
                this.model._logger.warn(
                    err,
                    "IntegrationsRender._initiateVersionControlAction() error."
                );
            })
            .finally(() => {
                this._statusChanged(this._model._versionControlConfigEntity.status);
            });
    }

    _statusChanged(val) {
        if (val === "INITIALIZING") {
            this._initiateVersionControl.setDisabled(true);
            this._initiateVersionControl.label = "Syncing data...";
        } else {
            this._initiateVersionControl.setDisabled(false);
            this._initiateVersionControl.label = "Sync Data";
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _onShutdown() {
        this._shutdown();
    }

    _resetScheduleToDefault() {
        const defaultSchedule = ScheduleComponent.defaultValue({
            allowOne: false,
            allowRepeat: true,
            pollOnly: false,
            availOnly: true,
            noStartingEnding: true,
        });

        Object.assign(defaultSchedule, {time: {hour: 23, minute: 59}});

        this._scheduleComponent.setSchedule(defaultSchedule, false);
        // this._isDefaultSchedule = true;
        return true;
    }

    _setVendorConfig(isConfigEnabled, type = "cisco") {
        if (type === "nist") {
            this.model.vendorApiConfig.setVendorConfig("nist", {
                enabled: isConfigEnabled,
                apiKey: this._nistCredentialsCmbBox.value,
            });
        } else if (type === "ss") {
            this.model.vendorApiConfig.setVendorConfig("stackstorm", {
                enabled: isConfigEnabled,
                apiKey: this._ssCredentialsCmbBox.value,
                apiUrl: this._ssUrlInput.value,
                ssl: this._ssUseSSL.value,
                skipIdentityCheck: this._ssSkipServerCheck.value,
                customCACertificate: this._ssCACert.value,
            });
        } else {
            this.model.vendorApiConfig.setVendorConfig("cisco", {
                enabled: isConfigEnabled,
                serviceConnectionId: this._ciscoConnectionComboBox.value,
            });
        }
    }

    _setGitSupportData() {
        const gitLocalEnabled = this._gsEnableLocalGitCheckbox.getValue();
        const gitRemoteEnabled = this._gsEnableRemoteGitCheckbox.getValue();
        const gitRemoteConnection = this._gRCCmbBox.getValue();
        const gitScheduleEnabled = this._gsEnableScheduleCheckbox.getValue();
        const gitSchedule = this._gRScheduleComponent.getSchedule();

        this._model._versionControlConfigEntity.setLocalEnabled(gitLocalEnabled);
        if (gitLocalEnabled) {
            this._model._versionControlConfigEntity.setRemoteEnabled(gitRemoteEnabled);
            if (gitRemoteEnabled) {
                this._model._versionControlConfigEntity.setServiceConnectionId(gitRemoteConnection);
            }

            this._model._versionControlConfigEntity.setScheduleEnabled(gitScheduleEnabled);
            if (gitScheduleEnabled) {
                this._model._versionControlConfigEntity.setSchedule(
                    gitSchedule || this._defaultSchedule
                );
            }
        }
        const isInitVersionControlDisabled = this._isInitVersionControlDisabled();
        if (this._model._versionControlConfigEntity.status === "INITIALIZING") {
            this._statusChanged("INITIALIZING");
        } else {
            this._initiateVersionControl.setDisabled(isInitVersionControlDisabled);
        }
        this._updateControlButtonsAvailability();
    }

    _onIntegrationsOrganizationChanged() {
        // this.model.modalUserFeedback.displayLoading();
        this.model
            .reloadData()
            .then(() => {
                this._ciscoEnableCheckbox.setDisabled(
                    this._model._ciscoServiceConnectionEntities.length === 0
                );
                if (this._model._ciscoServiceConnectionEntities.length > 0) {
                    this._ciscoNoConnectionsEl.classList.add("hidden");
                    this._ciscoConnectionContainer.classList.remove("hidden");
                    this._addNewConnectionButton.renderer.rootElement.classList.remove("hidden");
                    this._testCiscoConnectivity.renderer.rootElement.classList.remove("hidden");
                } else {
                    this._ciscoNoConnectionsEl.classList.remove("hidden");
                    this._ciscoConnectionContainer.classList.add("hidden");
                    this._addNewConnectionButton.renderer.rootElement.classList.add("hidden");
                    this._testCiscoConnectivity.renderer.rootElement.classList.add("hidden");
                }
                this._setFormValues();
            })
            .catch((err) => {
                this.model._logger.warn(
                    err,
                    "IntegrationsRenderer._onIntegrationsOrganizationChanged()"
                );
            });
        // .finally(() => {
        //     // this.model.modalUserFeedback.hideDisplayed();
        // });
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = IntegrationsRenderer;
